import React from "react";
import ColorLensOutlinedIcon from "@mui/icons-material/ColorLensOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import { Box, Typography } from "@mui/material";
import { useScreenshotTranslation } from "../../../lib/screenshotI18n";
import { PieceVisual, pieceVisualRenderSizePx } from "../board/PieceVisual";
import ScreenshotPhoneFrame from "./ScreenshotPhoneFrame";
import {
    boardFrameSx,
    promoHeadlineSx,
    promoNavy,
    sortingShellSx,
} from "../styles/screenshotStyles";

const JM = "mobileScreenshot.jigsaw";

function FloatingPiece({ pieceIndex, imageUrl, grid, paths, style }) {
    if (!paths || grid <= 0) return null;
    const cellPx = 500 / grid;
    const scale = 0.42;
    const size = Math.round(pieceVisualRenderSizePx(cellPx) * scale);
    return (
        <Box
            sx={{
                position: "absolute",
                width: size,
                height: size,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                filter: "drop-shadow(0 8px 14px rgba(15,23,42,0.28))",
                zIndex: 4,
                ...style,
            }}
        >
            <Box sx={{ transform: `scale(${scale})`, transformOrigin: "center", lineHeight: 0 }}>
                <PieceVisual
                    pieceIndex={pieceIndex}
                    imageUrl={imageUrl}
                    grid={grid}
                    paths={paths}
                    anchorToCell={false}
                />
            </Box>
        </Box>
    );
}

/**
 * Shot 03 — asymmetrical tilted phone (top-left) + sorting tray sliding from bottom-right.
 * `compact` — Facebook Ads 4:5: smaller board + higher tray so the tray stays in frame.
 */
export default function SortingAsymLayout({
    headline,
    board,
    pieceIndices = [],
    imageUrl,
    grid,
    paths,
    compact = false,
    landscape = false,
}) {
    const { t } = useScreenshotTranslation();
    const trayPieces = pieceIndices.slice(0, 8);
    const floatPieces = pieceIndices.slice(0, 4);

    if (landscape) {
        const trayPiecesPc = pieceIndices.slice(0, 9);
        const trayScale = 2.7;
        return (
            <Box
                sx={{
                    ...sortingShellSx,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 3,
                    px: 3,
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        flex: "0 1 34%",
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        gap: 2,
                    }}
                >
                    <Typography
                        component="h1"
                        sx={{
                            ...promoHeadlineSx,
                            fontWeight: 800,
                            fontSize: "2.6rem",
                            color: promoNavy,
                            textAlign: "left",
                        }}
                    >
                        {headline}
                    </Typography>
                    <ScreenshotPhoneFrame sx={{ width: "100%", maxWidth: 420 }}>
                        <Box sx={{ ...boardFrameSx, aspectRatio: "1" }}>{board}</Box>
                    </ScreenshotPhoneFrame>
                </Box>

                <Box
                    sx={{
                        flex: "1 1 62%",
                        alignSelf: "stretch",
                        minWidth: 0,
                        my: 2,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 3,
                        p: 3,
                        bgcolor: "rgba(255,255,255,0.94)",
                        border: "1px solid rgba(148,163,184,0.4)",
                        boxShadow: "0 22px 48px rgba(15,23,42,0.16)",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "2.1rem",
                            fontWeight: 800,
                            color: promoNavy,
                            mb: 2,
                        }}
                    >
                        {t(`${JM}.pieces-tray-label`, "Pieces Tray")}
                    </Typography>
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            alignContent: "center",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        {trayPiecesPc.map((idx) => {
                            const cellPx = 500 / Math.max(grid, 1);
                            const size = Math.round(pieceVisualRenderSizePx(cellPx) * trayScale);
                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        width: size,
                                        height: size,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        filter: "drop-shadow(0 8px 14px rgba(15,23,42,0.22))",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            transform: `scale(${trayScale})`,
                                            transformOrigin: "center",
                                            lineHeight: 0,
                                        }}
                                    >
                                        <PieceVisual
                                            pieceIndex={idx}
                                            imageUrl={imageUrl}
                                            grid={grid}
                                            paths={paths}
                                            anchorToCell={false}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                ...sortingShellSx,
                position: "relative",
                flexDirection: landscape ? "row" : "column",
                ...(compact
                    ? {
                          pt: "max(env(safe-area-inset-top, 0px), 8px)",
                          pb: "max(env(safe-area-inset-bottom, 0px), 8px)",
                      }
                    : {}),
            }}
        >
            <Box sx={{ px: 2, pt: compact ? 0.25 : 0.5, pb: compact ? 0.5 : 1, textAlign: "left", width: landscape ? "36%" : "100%", alignSelf: landscape ? "center" : undefined }}>
                <Typography
                    component="h1"
                    sx={{
                        ...promoHeadlineSx,
                        fontWeight: 800,
                        fontSize: { xs: compact ? "1.55rem" : "1.85rem", sm: compact ? "1.7rem" : "2.05rem" },
                        color: promoNavy,
                        textAlign: "left",
                    }}
                >
                    {headline}
                </Typography>
            </Box>

            <Box
                sx={{
                    position: "relative",
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                }}
            >
                {/* Tilted phone — near full-width board (smaller when compact) */}
                <Box
                    sx={{
                        position: "absolute",
                        top: landscape ? "6%" : compact ? "2%" : "8%",
                        left: landscape ? "8%" : "1%",
                        width: landscape ? "78%" : compact ? "72%" : "92%",
                        maxWidth: "100%",
                        transform: landscape ? "none" : "rotate(-7deg)",
                        transformOrigin: "top left",
                        zIndex: 2,
                    }}
                >
                    <ScreenshotPhoneFrame sx={{ maxWidth: "100%" }}>
                        <Box sx={boardFrameSx}>{board}</Box>
                    </ScreenshotPhoneFrame>
                </Box>

                {/* Floating pieces mid-sort toward tray */}
                {floatPieces[0] != null ? (
                    <FloatingPiece
                        pieceIndex={floatPieces[0]}
                        imageUrl={imageUrl}
                        grid={grid}
                        paths={paths}
                        style={{
                            top: compact ? "28%" : "38%",
                            left: compact ? "52%" : "48%",
                            transform: "rotate(-18deg)",
                        }}
                    />
                ) : null}
                {floatPieces[1] != null ? (
                    <FloatingPiece
                        pieceIndex={floatPieces[1]}
                        imageUrl={imageUrl}
                        grid={grid}
                        paths={paths}
                        style={{
                            top: compact ? "34%" : "48%",
                            left: compact ? "64%" : "58%",
                            transform: "rotate(12deg)",
                        }}
                    />
                ) : null}
                {floatPieces[2] != null ? (
                    <FloatingPiece
                        pieceIndex={floatPieces[2]}
                        imageUrl={imageUrl}
                        grid={grid}
                        paths={paths}
                        style={{
                            top: compact ? "40%" : "56%",
                            left: compact ? "46%" : "42%",
                            transform: "rotate(8deg)",
                        }}
                    />
                ) : null}
                {floatPieces[3] != null ? (
                    <FloatingPiece
                        pieceIndex={floatPieces[3]}
                        imageUrl={imageUrl}
                        grid={grid}
                        paths={paths}
                        style={{
                            top: compact ? "46%" : "62%",
                            left: compact ? "68%" : "62%",
                            transform: "rotate(-10deg)",
                        }}
                    />
                ) : null}

                {/* Sorting tray — slides in from bottom-right (higher when compact) */}
                <Box
                    sx={{
                        position: "absolute",
                        right: compact ? "-2%" : "-4%",
                        bottom: compact ? "42%" : "14%",
                        width: compact ? "66%" : "72%",
                        maxWidth: 300,
                        zIndex: 3,
                        transform: "rotate(4deg)",
                        borderRadius: 3,
                        p: compact ? 1 : 1.25,
                        bgcolor: "rgba(255,255,255,0.78)",
                        border: "1px solid rgba(148,163,184,0.4)",
                        boxShadow: "0 22px 48px rgba(15,23,42,0.22)",
                        backdropFilter: "blur(12px)",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.68rem",
                            fontWeight: 800,
                            letterSpacing: "0.08em",
                            textTransform: "none",
                            color: "#94a3b8",
                            mb: 1,
                            px: 0.5,
                        }}
                    >
                        {t(`${JM}.sorting-tray-label`, "Sorting Tray")}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.75,
                            justifyContent: "flex-start",
                            minHeight: compact ? 56 : 72,
                        }}
                    >
                        {trayPieces.map((idx) => {
                            const cellPx = 500 / Math.max(grid, 1);
                            const scale = 0.32;
                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        width: Math.round(pieceVisualRenderSizePx(cellPx) * scale),
                                        height: Math.round(pieceVisualRenderSizePx(cellPx) * scale),
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        filter: "drop-shadow(0 3px 6px rgba(15,23,42,0.18))",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            transform: `scale(${scale})`,
                                            transformOrigin: "center",
                                            lineHeight: 0,
                                        }}
                                    >
                                        <PieceVisual
                                            pieceIndex={idx}
                                            imageUrl={imageUrl}
                                            grid={grid}
                                            paths={paths}
                                            anchorToCell={false}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {/* Minimalist toolbar — Color Filter active */}
            <Box
                sx={{
                    flexShrink: 0,
                    mx: 2,
                    mb: "max(env(safe-area-inset-bottom, 0px), 14px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    px: 1,
                    py: 1,
                    borderRadius: 999,
                    bgcolor: "rgba(255,255,255,0.92)",
                    border: "1px solid rgba(148,163,184,0.35)",
                    boxShadow: "0 8px 22px rgba(15,23,42,0.1)",
                }}
            >
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.65,
                        px: 1.75,
                        py: 0.85,
                        borderRadius: 999,
                        bgcolor: promoNavy,
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: "0.78rem",
                        whiteSpace: "nowrap",
                    }}
                >
                    <ColorLensOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
                    {t(`${JM}.tool-color-filter`, "Color Filter")}
                </Box>
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.5,
                        py: 0.85,
                        borderRadius: 999,
                        color: "#64748b",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        whiteSpace: "nowrap",
                    }}
                >
                    <ZoomInOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
                    {t(`${JM}.tool-zoom`, "Zoom")}
                </Box>
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.5,
                        py: 0.85,
                        borderRadius: 999,
                        color: "#64748b",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        whiteSpace: "nowrap",
                    }}
                >
                    <ExtensionOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
                    {t(`${JM}.tool-edges-short`, "Edges")}
                </Box>
            </Box>
        </Box>
    );
}
